import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { resolveUnitPrecision } from './formatter-precision';
import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Formatter for time values.
 * Formats time (sec, min, hrs) to the display units.
 *
 * @param {UnitValue} unitValue - The time value and units to format
 * @param {any} unitValue.value - The numeric time value
 * @param {string} [unitValue.units] - The current units of the value (typically 'sec')
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 *
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted time value (e.g. '10min')
 *   - unitsLabel: The unit string, empty string as time values have no units
 *   - valueUnitsLabel: Combined value and units with proper spacing
 *
 * @example
 * formatTime({ value: 3600, units: 'sec' }, 'HH:MM')
 * // Returns: { valueLabel: '01:00', unitsLabel: '', valueUnitsLabel: '01:00' }
 *
 * @example
 * formatTime({ value: 60, units: 'min' }, 'HH:MM')
 * // Returns: { valueLabel: '01:00', unitsLabel: '', valueUnitsLabel: '01:00' }
 *
 * @example
 * formatTime({ value: 1, units: 'hrs' }, 'HH:MM')
 * // Returns: { valueLabel: '01:00', unitsLabel: '', valueUnitsLabel: '01:00' }
 */
export const formatTime = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  // If the unit value is undefined, empty, or none, set the units to sec
  // This is to ensure that the sec transformer is run
  if (isNullOrUndefined(unitValue.units) || unitValue.units === '' || unitValue.units === 'none') {
    unitValue.units = SUPPORTED_UNITS.SEC;
  }

  const transformed = transformUnits(unitValue, displayUnits);
  const precision = resolveUnitPrecision({
    units: transformed.units,
    fallbackPrecision: 0,
  });

  return unitValueToFormattedUnitLabels({
    value: formatNumber(transformed.value, precision),
    units: transformed.units,
  });
};
